import React from "react"
import { useParams, useNavigate } from "react-router-dom"
import logo from "../images/horizontal-light.svg"
import MobileHomeIcon from "../icons/MobileHomeIcon.png"
import ProfileMenu from "./ProfileMenu"
import { DatasetSchemas, Datasets, Schemas, CurrentUser } from "../lib/store"
import {
  Avatar,
  Box,
  Flex,
  Image,
  Text,
  Tooltip,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from "@chakra-ui/react"
import Link from "./Link"
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons"

function DatasetTree() {
  const [datasetSchemas] = DatasetSchemas.useState()
  const [datasets] = Datasets.useState()
  const [schemas] = Schemas.useState()
  const { datasetId } = useParams()
  const navigate = useNavigate()
  const [expanded, setExpanded] = React.useState({})

  React.useEffect(() => {
    if (datasetId) {
      setExpanded((prev) => ({ ...prev, [datasetId]: true }))
    }
  }, [datasetId])

  const toggleExpand = (id, e) => {
    e.stopPropagation()
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <Box
      flex={1}
      overflowY="auto"
      py={2}
      css={{ "&::-webkit-scrollbar": { display: "none" } }}
    >
      <Link to="/datasets">
        <Text
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          color="Primary.200"
          px={3}
          pb={1}
          letterSpacing="wider"
          _hover={{ color: "Primary.100" }}
        >
          Datasets
        </Text>
      </Link>
      {Object.values(datasets).map((dataset) => {
        const isExpanded = expanded[dataset.id]
        const isActive = dataset.id === datasetId
        const schemaIds = datasetSchemas[dataset.id] || []

        return (
          <Box key={dataset.id}>
            <Flex
              align="center"
              px={2}
              py={0.5}
              mx={1}
              cursor="pointer"
              bg={isActive ? "Primary.800" : "transparent"}
              _hover={{ bg: "Primary.600" }}
              borderRadius="sm"
              onClick={() => navigate(`/datasets/${dataset.id}`)}
            >
              <Box
                as="button"
                onClick={(e) => toggleExpand(dataset.id, e)}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w={4}
                h={4}
                flexShrink={0}
                opacity={schemaIds.length > 0 ? 1 : 0.3}
              >
                {isExpanded ? (
                  <ChevronDownIcon boxSize={3} color="white" />
                ) : (
                  <ChevronRightIcon boxSize={3} color="white" />
                )}
              </Box>
              <Text
                fontSize="xs"
                color="white"
                ml={1}
                flex={1}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {dataset.name}
              </Text>
            </Flex>
            {isExpanded &&
              schemaIds.map((id) => {
                const schema = schemas[id]
                if (!schema) return null
                return (
                  <Box
                    key={id}
                    pl={7}
                    pr={2}
                    py={0.5}
                    mx={1}
                    cursor="pointer"
                    _hover={{ bg: "Primary.600" }}
                    borderRadius="sm"
                  >
                    <Link to={`/datasets/${dataset.id}/schemas/${id}/records`}>
                      <Text
                        fontSize="xs"
                        color="whiteAlpha.800"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {schema.name}
                      </Text>
                    </Link>
                  </Box>
                )
              })}
          </Box>
        )
      })}
    </Box>
  )
}

function MobileSidebar() {
  const [datasets] = Datasets.useState()
  const [schemas] = Schemas.useState()
  const [datasetSchemas] = DatasetSchemas.useState()
  const { datasetId, schemaId } = useParams()
  const navigate = useNavigate()

  const selectedDataset = datasets[datasetId]
  const selectedSchema = schemas[schemaId]

  return (
    <Flex
      flexDirection="row"
      height={16}
      width="100%"
      alignItems="center"
      bg="Primary.700"
    >
      <Box flexShrink={0}>
        <Link to="/" bg="Primary.800">
          <Image
            width={12}
            mx={4}
            my="auto"
            src={MobileHomeIcon}
            alt="Blind Insight Logo"
            display="block"
          />
        </Link>
      </Box>
      <Flex
        gap={2}
        justifyContent="flex-start"
        alignItems="center"
        mr={4}
        minWidth="0"
        overflow="hidden"
      >
        <Menu minWidth="0">
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            minWidth="0"
            size="sm"
          >
            <Box
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
              pr={2}
            >
              {selectedDataset?.name || "Dataset"}
            </Box>
          </MenuButton>
          <MenuList>
            {Object.values(datasets).map((dataset) => (
              <MenuItem
                key={dataset.id}
                onClick={() => navigate(`/datasets/${dataset.id}`)}
              >
                {dataset.name}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        <Menu minWidth="0">
          <MenuButton
            size="sm"
            as={Button}
            rightIcon={<ChevronDownIcon />}
            isDisabled={!datasetId}
            minWidth="0"
          >
            <Box
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
              pr={2}
            >
              {selectedSchema?.name || "Schema"}
            </Box>
          </MenuButton>
          <MenuList>
            {!datasetId ? (
              <MenuItem isDisabled>Please select dataset first</MenuItem>
            ) : (
              datasetSchemas[datasetId]?.map((id) => (
                <MenuItem
                  key={id}
                  onClick={() =>
                    navigate(`/datasets/${datasetId}/schemas/${id}/records`)
                  }
                >
                  {schemas[id]?.name}
                </MenuItem>
              ))
            )}
          </MenuList>
        </Menu>
      </Flex>
      <ProfileMenu />
    </Flex>
  )
}

export default function Sidebar({
  isCollapsed,
  onToggleCollapse,
  width,
  onResizeStart,
  isResizing,
}) {
  const [currentUser] = CurrentUser.useState()
  const navigate = useNavigate()
  const isMobile = useBreakpointValue({ base: true, md: false })

  const logoSrc = isCollapsed ? MobileHomeIcon : logo

  const userInitial = React.useMemo(() => {
    if (currentUser?.first_name) return currentUser.first_name[0].toUpperCase()
    if (currentUser?.email) return currentUser.email[0].toUpperCase()
    return "?"
  }, [currentUser])

  if (isMobile) return <MobileSidebar />

  return (
    <Flex
      position="relative"
      flexDirection="column"
      height="100vh"
      width={`${width}px`}
      minWidth={`${width}px`}
      bg="Primary.700"
      transition={isResizing ? "none" : "width 0.2s ease, min-width 0.2s ease"}
      overflow="hidden"
    >
      <Flex
        flexShrink={0}
        bg="Primary.800"
        align="center"
        justify="space-between"
        px={isCollapsed ? 2 : 3}
        py={3}
        minH={12}
      >
        <Link to="/">
          <Image
            src={logoSrc}
            alt="Blind Insight"
            w={isCollapsed ? 8 : "auto"}
            maxH={isCollapsed ? "auto" : 5}
            objectFit="contain"
            display="block"
            pr={3}
          />
        </Link>
        <Box
          as="button"
          onClick={onToggleCollapse}
          bg="Primary.900"
          color="Primary.100"
          _hover={{ color: "Primary.900", bg: "Primary.100" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          boxSize={4}
          borderRadius="md"
          fontSize="lg"
          fontWeight="bold"
          transition="all 0.15s"
          position="absolute"
          right={0}
          p={3}
        >
          {isCollapsed ? "»" : "«"}
        </Box>
      </Flex>

      {isCollapsed ? (
        <Flex direction="column" align="center" flex={1}>
          <Box flex={1} />
          <Tooltip label={currentUser?.email || "Account"} placement="right">
            <Avatar
              size="sm"
              name={userInitial}
              bg="Primary.400"
              color="white"
              cursor="pointer"
              onClick={() => navigate("/account")}
              mb={3}
            />
          </Tooltip>
        </Flex>
      ) : (
        <>
          <DatasetTree />
          <ProfileMenu />
        </>
      )}

      {!isCollapsed && (
        <Box
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          w="4px"
          cursor="col-resize"
          _hover={{ bg: "Primary.400" }}
          transition="background 0.15s"
          onMouseDown={onResizeStart}
          zIndex={10}
        />
      )}
    </Flex>
  )
}
